import React, { useState } from "react";
import { AnswersType, FlagsType, ParticipantExamQuestion } from "../../../types/question";
import { Button } from "../../atom/Button";
import { useTranslation } from "react-i18next";
import { TRANSLATION_CONFIG } from "../../../utils/consts/translation";
import { ToolTip } from "../../atom/Tooltip";
import { SummaryStateType } from "../../../types/components";
import { usePutResource } from "../../../services";
import { ArrowRight, BookmarkAdd, BookmarkFilled } from "@carbon/icons-react";

interface QuestionProps {
  currentQuestion: number;
  questions: ParticipantExamQuestion[];
  answers: AnswersType;
  toggleFlag: (questionNumber?: number) => void;
  flags: FlagsType;
  handleOptionChange: (answer: string | null) => void;
  setCurrentQuestion: React.Dispatch<React.SetStateAction<number>>;
  flagHoverCount: number;
  setFlagHoverCount: React.Dispatch<React.SetStateAction<number>>;
  setIsSummary: React.Dispatch<React.SetStateAction<SummaryStateType>>;
}

export const Questions = ({
  currentQuestion,
  questions,
  answers,
  toggleFlag,
  flags,
  handleOptionChange,
  setCurrentQuestion,
  setFlagHoverCount,
  setIsSummary,
}: QuestionProps) => {
  const { t } = useTranslation(TRANSLATION_CONFIG.appNamespace);
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  const { updateResource } = usePutResource(`/common/participant-exam-question`);

  const filledFlag = flags[questions[currentQuestion]?.id];
  const question = questions[currentQuestion];
  const isFirst = currentQuestion === 0;

  const goToNextQuestion = () => {
    if (currentQuestion <= questions.length) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleMouseEnter = () => {
    //if (flagHoverCount < 3)
    setTooltipVisible(true);
    setFlagHoverCount((prev) => prev + 1);
  };

  const handleRadioClick = (option: string) => {
    const currentAnswer = answers[questions[currentQuestion]?.id];

    if (currentAnswer !== option) {
      return;
    }

    handleOptionChange(null);

    updateResource({
      resourceId: question.id,
      selected_answer_id: null,
    });
  };

  const handleRadioChange = (option: string) => {
    const currentAnswer = answers[questions[currentQuestion]?.id];
    const exam_question = question?.exam_question;
    const answerId = exam_question?.exam_answers.find(
      ({ answer_text }) => answer_text === option,
    )?.id;

    if (currentAnswer === option) {
      return;
    }

    handleOptionChange(option);

    updateResource({
      resourceId: question?.id,
      selected_answer_id: answerId,
    });
  };

  const TooltipAdditional = () => {
    return (
      <p
        onClick={() => {
          setIsSummary({
            state: true,
            filter: "flagged",
          });
        }}
        className="text-blue-750 font-extrabold text-[13px] leading-[18.2px] tracking-[0.25px] underline pt-2">
        {t("View all flagged questions")}
      </p>
    );
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="pl-10 pr-7 py-6" onMouseLeave={() => setTooltipVisible(false)}>
        <div className="flex justify-between items-center">
          <p className="text-[16px] font-light leading-[20.16px] tracking-[2px]">{`${currentQuestion + 1} / ${questions.length}`}</p>
          <div
            className="cursor-pointer relative"
            onMouseEnter={handleMouseEnter}
            onClick={() => toggleFlag()}>
            {filledFlag ? (
              <BookmarkFilled size={24} className="text-red-450" />
            ) : (
              <BookmarkAdd size={24} className="text-gray-376 hover:text-blue-750" />
            )}
            {isTooltipVisible && (
              <ToolTip
                text={t("Use the flag to mark questions and return to them later.")}
                additionalComponent={<TooltipAdditional />}
              />
            )}
          </div>
        </div>
        <p className="mt-8 text-[26px] font-medium leading-[32.5px] tracking-[0.1px] text-left text-black w-5/6">
          {question?.exam_question?.question_text}
        </p>
      </div>

      {/* Content Section */}
      <div className="flex flex-col justify-start items-start flex-grow px-4 py-4 overflow-y-auto w-5/6 pl-10 mt-2.5">
        <div className="space-y-3 w-full">
          {question?.exam_question?.exam_answers?.map((option, index) => {
            const isChecked = answers[questions[currentQuestion]?.id] === option.answer_text;

            return (
              <label
                key={index}
                className={`flex items-center pt-[14px] pr-[40px] pb-[16px] pl-[16px] rounded-lg cursor-pointer transition border-[2px] border-white ${
                  isChecked
                    ? "!border-ominimo-400 text-ominimo-400 bg-white border-[2px]"
                    : "bg-white hover:border-ominimo-405-transparent hover:border-[2px] text-gray-800"
                }`}>
                {/* Hidden Radio Input */}
                <input
                  type="radio"
                  name={`question-${currentQuestion}`}
                  value={option.answer_text}
                  checked={isChecked}
                  onClick={() => handleRadioClick(option.answer_text)}
                  onChange={() => handleRadioChange(option.answer_text)}
                  className="hidden"
                />
                <div className="flex gap-1 w-full">
                  {/* Custom Styled Radio Button */}
                  <div
                    className={`w-5 h-5 flex items-center justify-center rounded-full border-[2px] transition shrink-0 ${
                      isChecked ? "border-ominimo-400" : "border-gray-200"
                    }`}>
                    {isChecked && <div className="w-2.5 h-2.5 bg-ominimo-400 rounded-full"></div>}
                  </div>

                  {/* Option Text */}
                  <p className="ml-3 text-[16px] font-normal leading-[22.4px] tracking-[0.2px] text-left">
                    {option.answer_text}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Buttons Section */}
      <div className="flex justify-between items-center border-t border-gray-200 px-7 pt-7 pb-8 h-[104px]">
        <Button
          type="tertiary"
          label={t("Back")}
          onClick={goToPreviousQuestion}
          isDisabled={isFirst}
        />
        <Button
          type="primary"
          label={t("Next")}
          onClick={goToNextQuestion}
          leftIcon={<ArrowRight size={20} />}
          isDisabled={currentQuestion === questions.length - 1}
        />
      </div>
    </div>
  );
};
